/**
 * Dialoghi single: media players powered by Plyr.
 *
 * @package HelloElementorChild
 */

import Plyr from 'plyr';

var AUDIO_SPEEDS = [1, 1.25, 1.5, 1.75, 2];
var playerInstances = new WeakMap();

function escapeAttribute(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function getDialoghiConfig() {
  return window.camstDialoghiSingleConfig || {};
}

var WAVEFORM_HEIGHTS = [
  35, 55, 80, 60, 90, 45, 70, 55, 85, 40,
  65, 50, 95, 75, 55, 80, 45, 70, 60, 90,
  50, 75, 40, 65, 85, 55, 70, 45, 80, 60,
  90, 50, 65, 75, 40, 85, 55, 70, 45, 60
];

function buildWaveformBars() {
  var bars = '';
  for (var i = 0; i < WAVEFORM_HEIGHTS.length; i++) {
    var h = WAVEFORM_HEIGHTS[i];
    bars += '<span class="camst-dialoghi-single__audio-waveform-bar" aria-hidden="true" style="height:' + h + '%"></span>';
  }
  return bars;
}

// Colori waveform: chiaro a riposo, grigio scuro come preview hover, rosso = riprodotto.
var WAVEFORM_COLOR_LIGHT = '#e1e1e1';
var WAVEFORM_COLOR_HOVER = '#9a9a9a';
var WAVEFORM_COLOR_PLAYED = 'var(--camst-red, #e30613)';

function colorWaveform(bars, playedRatio, hoverRatio) {
  for (var i = 0; i < bars.length; i++) {
    var centerRatio = (i + 0.5) / bars.length; // rosso: a 0:00 nessuna barra accesa
    var leftRatio = i / bars.length;           // hover: il grigio scuro arriva fino al cursore
    var color;

    if (centerRatio <= playedRatio) {
      color = WAVEFORM_COLOR_PLAYED;
    } else if (hoverRatio >= 0 && leftRatio < hoverRatio) {
      color = WAVEFORM_COLOR_HOVER;
    } else {
      color = WAVEFORM_COLOR_LIGHT;
    }

    bars[i].style.backgroundColor = color;
  }
}

function getAudioControlsMarkup(title) {
  var safeTitle = escapeAttribute(title || '');

  // Solo lo stage (play + waveform + time). La bar è iniettata nel footer PHP separato.
  return (
    '<div class="camst-dialoghi-single__audio-plyr-controls plyr__controls">' +
      '<div class="camst-dialoghi-single__audio-stage">' +
        '<button type="button" class="camst-dialoghi-single__audio-play plyr__control" data-plyr="play" aria-label="Riproduci ' + safeTitle + '">' +
          '<span class="camst-dialoghi-single__audio-icon camst-dialoghi-single__audio-icon--pause icon--pressed" aria-hidden="true"><span></span><span></span></span>' +
          '<span class="camst-dialoghi-single__audio-icon camst-dialoghi-single__audio-icon--play icon--not-pressed" aria-hidden="true"></span>' +
          '<span class="screen-reader-text label--pressed">Pausa</span>' +
          '<span class="screen-reader-text label--not-pressed">Riproduci</span>' +
        '</button>' +
        '<div class="camst-dialoghi-single__audio-progress-wrap">' +
          '<div class="camst-dialoghi-single__audio-waveform" aria-hidden="true">' + buildWaveformBars() + '</div>' +
          '<div class="camst-dialoghi-single__audio-progress plyr__progress">' +
            '<input data-plyr="seek" type="range" min="0" max="100" step="0.01" value="0" aria-label="Posizione riproduzione">' +
            '<progress class="plyr__progress__buffer" min="0" max="100" value="0">% buffered</progress>' +
            '<span role="tooltip" class="plyr__tooltip">00:00</span>' +
          '</div>' +
          '<div class="camst-dialoghi-single__audio-time">' +
            '<span class="plyr__time plyr__time--current" aria-label="Tempo corrente">00:00</span>' +
            '<span aria-hidden="true">|</span>' +
            '<span class="plyr__time plyr__time--duration" aria-label="Durata">00:00</span>' +
          '</div>' +
        '</div>' +
      '</div>' +
    '</div>'
  );
}

function getAudioBarMarkup() {
  return (
    '<div class="camst-dialoghi-single__audio-bar">' +
      '<button type="button" class="camst-dialoghi-single__audio-skip" data-camst-dialoghi-audio-rewind="15" aria-label="Indietro 15 secondi">' +
        '<span class="camst-dialoghi-single__audio-skip-icon" aria-hidden="true">↺</span><span>15</span>' +
      '</button>' +
      '<button type="button" class="camst-dialoghi-single__audio-skip" data-camst-dialoghi-audio-forward="30" aria-label="Avanti 30 secondi">' +
        '<span class="camst-dialoghi-single__audio-skip-icon" aria-hidden="true">↻</span><span>30</span>' +
      '</button>' +
      '<button type="button" class="camst-dialoghi-single__audio-rate" data-camst-dialoghi-audio-rate aria-label="Velocita riproduzione">1x</button>' +
      '<button type="button" class="camst-dialoghi-single__audio-chapters" data-camst-dialoghi-audio-chapters aria-label="Capitoli">&#9776;</button>' +
      '<div class="camst-dialoghi-single__audio-bar-spacer"></div>' +
      '<button type="button" class="camst-dialoghi-single__audio-info" data-camst-dialoghi-audio-info aria-label="Informazioni">Informazioni</button>' +
      '<button type="button" class="camst-dialoghi-single__audio-share" data-camst-dialoghi-audio-share aria-label="Condividi">Condividi</button>' +
    '</div>'
  );
}

function setupWaveform(waveformEl, bars, player) {
  var hoverRatio = -1;
  var isDragging = false;

  function playedRatio() {
    return player.duration ? Math.max(0, Math.min(1, player.currentTime / player.duration)) : 0;
  }

  function render() {
    colorWaveform(bars, playedRatio(), hoverRatio);
  }

  function ratioFromEvent(e) {
    var clientX = e.touches ? e.touches[0].clientX : e.clientX;
    var rect = waveformEl.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  }

  function seek(e) {
    var ratio = ratioFromEvent(e);
    hoverRatio = ratio;

    if (player.duration) {
      player.currentTime = ratio * player.duration;
    }

    render();
  }

  // Hover: preview grigio scuro tra il rosso e il cursore
  waveformEl.addEventListener('mousemove', function (e) {
    if (isDragging) { return; }
    hoverRatio = ratioFromEvent(e);
    render();
  });

  waveformEl.addEventListener('mouseleave', function () {
    if (isDragging) { return; }
    hoverRatio = -1;
    render();
  });

  // Click + drag per cercare
  waveformEl.addEventListener('mousedown', function (e) {
    isDragging = true;
    seek(e);
  });

  waveformEl.addEventListener('touchstart', function (e) {
    e.preventDefault();
    isDragging = true;
    seek(e);
  }, { passive: false });

  document.addEventListener('mousemove', function (e) {
    if (isDragging) { seek(e); }
  });

  document.addEventListener('touchmove', function (e) {
    if (!isDragging) { return; }
    e.preventDefault();
    seek(e);
  }, { passive: false });

  document.addEventListener('mouseup', function () {
    isDragging = false;
  });

  document.addEventListener('touchend', function () {
    isDragging = false;
    hoverRatio = -1;
    render();
  });

  player.on('timeupdate', render);
  player.on('seeked', render);
  player.on('ended', function () {
    hoverRatio = -1;
    render();
  });

  render();
}

function bindAudioActions(player, rootEl) {
  var container = player.elements && player.elements.container;
  // I bottoni della bar vivono nel footer (fuori da container Plyr): cerchiamo in rootEl
  var scope = rootEl || container;

  if (!scope) {
    return;
  }

  var rateButton = scope.querySelector('[data-camst-dialoghi-audio-rate]');
  var rewindButton = scope.querySelector('[data-camst-dialoghi-audio-rewind]');
  var forwardButton = scope.querySelector('[data-camst-dialoghi-audio-forward]');
  var shareButton = scope.querySelector('[data-camst-dialoghi-audio-share]');
  var infoButton = scope.querySelector('[data-camst-dialoghi-audio-info]');
  var chaptersButton = scope.querySelector('[data-camst-dialoghi-audio-chapters]');

  function syncRateLabel() {
    if (!rateButton) {
      return;
    }

    rateButton.textContent = String(player.speed).replace(/\.0$/, '') + 'x';
  }

  if (rewindButton) {
    rewindButton.addEventListener('click', function () {
      player.rewind(15);
    });
  }

  if (forwardButton) {
    forwardButton.addEventListener('click', function () {
      player.forward(30);
    });
  }

  if (rateButton) {
    rateButton.addEventListener('click', function () {
      var currentIndex = AUDIO_SPEEDS.indexOf(player.speed);
      var nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % AUDIO_SPEEDS.length;
      player.speed = AUDIO_SPEEDS[nextIndex];
      syncRateLabel();
    });
  }

  if (shareButton) {
    shareButton.addEventListener('click', function () {
      var shareData = { title: document.title, url: window.location.href };

      if (navigator.share) {
        navigator.share(shareData).catch(function () {});
        return;
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(shareData.url).catch(function () {});
      }
    });
  }

  if (infoButton) {
    infoButton.addEventListener('click', function () {
      var prose = document.querySelector('.camst-dialoghi-single__prose');
      if (prose) {
        prose.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  if (chaptersButton) {
    chaptersButton.setAttribute('disabled', 'disabled');
    chaptersButton.style.opacity = '0.4';
    chaptersButton.style.cursor = 'default';
  }

  var waveformBars = container
    ? Array.prototype.slice.call(container.querySelectorAll('.camst-dialoghi-single__audio-waveform-bar'))
    : [];

  var waveformEl = container
    ? container.querySelector('.camst-dialoghi-single__audio-waveform')
    : null;

  if (waveformEl && waveformBars.length) {
    setupWaveform(waveformEl, waveformBars, player);
  }

  player.on('ready', syncRateLabel);
  player.on('ratechange', syncRateLabel);
  syncRateLabel();
}

function createAudioPlayer(element) {
  var duration = parseInt(element.dataset.durationSeconds || '0', 10);
  var rootEl = element.closest('.camst-dialoghi-single__player--audio') || element.parentElement;

  // Inietta la bar nel footer PRIMA di creare Plyr (il footer è già nel DOM)
  var footer = rootEl ? rootEl.querySelector('[data-camst-dialoghi-audio-footer]') : null;
  if (footer && !footer.querySelector('.camst-dialoghi-single__audio-bar')) {
    footer.innerHTML = getAudioBarMarkup();
  }

  var player = new Plyr(element, {
    controls: getAudioControlsMarkup(element.dataset.title || ''),
    duration: duration > 0 ? duration : null,
    iconUrl: getDialoghiConfig().iconUrl || '',
    invertTime: false,
    keyboard: { focused: true, global: false },
    loadSprite: false,
    resetOnEnd: false,
    seekTime: 15,
    settings: [],
    tooltips: { controls: false, seek: false },
  });

  // Bind actions: player.elements.container è disponibile subito dopo new Plyr()
  bindAudioActions(player, rootEl);

  // Play/pause icon toggle esplicito (sicurezza extra oltre a Plyr CSS)
  var plyrContainer = player.elements && player.elements.container;

  player.on('play', function () {
    var btn = plyrContainer && plyrContainer.querySelector('[data-plyr="play"]');
    if (btn) { btn.setAttribute('aria-pressed', 'true'); }
  });

  player.on('pause', function () {
    var btn = plyrContainer && plyrContainer.querySelector('[data-plyr="play"]');
    if (btn) { btn.setAttribute('aria-pressed', 'false'); }
  });

  return player;
}

function createVideoPlayer(element) {
  var config = getDialoghiConfig();
  var options = {
    controls: ['play-large', 'play', 'progress', 'current-time', 'duration', 'mute', 'volume', 'fullscreen'],
    hideControls: false,
    iconUrl: config.iconUrl || '',
    invertTime: false,
    loadSprite: true,
    settings: ['speed'],
    tooltips: { controls: false, seek: true },
    vimeo: {
      byline: false,
      dnt: true,
      portrait: false,
      speed: true,
      title: false,
      transparent: false,
    },
    youtube: {
      iv_load_policy: 3,
      modestbranding: 1,
      noCookie: true,
      rel: 0,
      showinfo: 0,
    },
  };

  // Per YouTube/Vimeo il div ha data-plyr-provider + data-plyr-embed-id:
  // Plyr crea l'embed automaticamente all'init, niente source-setter (inaffidabile su div vuoto).
  var player = new Plyr(element, options);

  return player;
}

function initDialoghiPlayer(element) {
  if (!element || playerInstances.has(element)) {
    return;
  }

  var kind = element.dataset.mediaKind || 'audio';
  var player = kind === 'video' ? createVideoPlayer(element) : createAudioPlayer(element);

  playerInstances.set(element, player);
  element.dataset.camstDialoghiPlayerInit = '1';
}

function teardownDialoghiPlayer(element) {
  var instance = playerInstances.get(element);

  if (instance) {
    instance.destroy();
    playerInstances.delete(element);
  }

  delete element.dataset.camstDialoghiPlayerInit;
}

export function initCamstDialoghiSingle(scope) {
  var elements = [];

  if (!scope) {
    elements = Array.prototype.slice.call(document.querySelectorAll('[data-camst-dialoghi-player]'));
  } else if (scope.matches && scope.matches('[data-camst-dialoghi-player]')) {
    elements = [scope];
  } else {
    elements = Array.prototype.slice.call(scope.querySelectorAll('[data-camst-dialoghi-player]'));
  }

  elements.forEach(initDialoghiPlayer);
}

export function teardownCamstDialoghiSingle(scope) {
  if (!scope) {
    return;
  }

  var elements = scope.matches && scope.matches('[data-camst-dialoghi-player]')
    ? [scope]
    : Array.prototype.slice.call(scope.querySelectorAll('[data-camst-dialoghi-player]'));

  elements.forEach(teardownDialoghiPlayer);
}
